import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const AdjustDatesModal = ({ isOpen, onClose, onApply, saving, deliverables = [] }) => {
  const [mode, setMode] = useState('shift'); // 'shift' | 'specific' | 'sync'
  const [direction, setDirection] = useState('later'); // 'later' | 'earlier'
  const [days, setDays] = useState('');
  const [specificDate, setSpecificDate] = useState('');
  const [error, setError] = useState('');

  // Reset form every time the modal opens
  useEffect(() => {
    if (isOpen) {
      setMode('shift');
      setDirection('later');
      setDays('');
      setSpecificDate('');
      setError('');
    }
  }, [isOpen]);

  // Compute earliest start and latest end across all deliverables, tasks, subtasks
  const { earliestStart, latestEnd } = useMemo(() => {
    const allDates = { starts: [], ends: [] };
    const collectDates = (items) => {
      (items || [])?.forEach(item => {
        if (item?.startDate) allDates?.starts?.push(item?.startDate);
        if (item?.endDate) allDates?.ends?.push(item?.endDate);
        (item?.tasks || [])?.forEach(task => {
          if (task?.startDate) allDates?.starts?.push(task?.startDate);
          if (task?.endDate) allDates?.ends?.push(task?.endDate);
          (task?.subtasks || [])?.forEach(sub => {
            if (sub?.startDate) allDates?.starts?.push(sub?.startDate);
            if (sub?.endDate) allDates?.ends?.push(sub?.endDate);
          });
        });
      });
    };
    collectDates(deliverables);
    const earliestStart = allDates?.starts?.length > 0 ? allDates?.starts?.sort()?.[0] : null;
    const latestEnd = allDates?.ends?.length > 0 ? allDates?.ends?.sort()?.reverse()?.[0] : null;
    return { earliestStart, latestEnd };
  }, [deliverables]);

  if (!isOpen) return null;

  const handleApply = () => {
    setError('');
    if (mode === 'shift') {
      const d = parseInt(days, 10);
      if (!days || isNaN(d) || d <= 0) {
        setError('Please enter a valid number of days (greater than 0).');
        return;
      }
      onApply({ mode: 'shift', days: direction === 'later' ? d : -d });
    } else if (mode === 'specific') {
      if (!specificDate) {
        setError('Please select a specific start date.');
        return;
      }
      onApply({ mode: 'specific', date: specificDate });
    } else if (mode === 'sync') {
      if (!earliestStart && !latestEnd) {
        setError('No task dates found to sync from.');
        return;
      }
      onApply({ mode: 'sync', earliestStart, latestEnd });
    }
  };

  const handleClose = () => {
    onClose();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr + 'T00:00:00')?.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative bg-card border border-border rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Icon name="CalendarClock" size={18} className="text-accent" />
            <h2 className="text-base font-semibold text-foreground">Adjust Project Dates</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          <p className="text-sm text-muted-foreground">
            Shift all deliverables, tasks, and subtasks by a number of days, set a new project start date, or sync the project dates to match the earliest and latest task dates.
          </p>

          {/* Mode selector */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setMode('shift')}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                mode === 'shift'
                  ? 'bg-accent/10 border-accent text-accent' : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name="ArrowLeftRight" size={15} />
              Shift by Days
            </button>
            <button
              onClick={() => setMode('specific')}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                mode === 'specific'
                  ? 'bg-accent/10 border-accent text-accent' : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name="CalendarDays" size={15} />
              Set Start Date
            </button>
            <button
              onClick={() => setMode('sync')}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                mode === 'sync' ?'bg-accent/10 border-accent text-accent' : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name="RefreshCw" size={15} />
              Sync to Tasks
            </button>
          </div>

          {mode === 'shift' ? (
            <div className="space-y-4">
              {/* Direction */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Direction</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setDirection('later')}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                      direction === 'later' ? 'bg-blue-50 border-blue-400 text-blue-700' : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name="ArrowRight" size={14} />
                    Start Later
                  </button>
                  <button
                    onClick={() => setDirection('earlier')}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                      direction === 'earlier' ? 'bg-green-50 border-green-400 text-green-700' : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name="ArrowLeft" size={14} />
                    Start Earlier
                  </button>
                </div>
              </div>

              {/* Days input */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Number of Days
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    value={days}
                    onChange={(e) => setDays(e?.target?.value)}
                    placeholder="e.g. 7"
                    className="w-full px-4 py-2.5 bg-input border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">days</span>
                </div>
                {days && !isNaN(parseInt(days, 10)) && parseInt(days, 10) > 0 && (
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    All dated items will start{' '}
                    <span className="font-medium text-foreground">{parseInt(days, 10)} day{parseInt(days, 10) !== 1 ? 's' : ''}</span>{' '}
                    {direction === 'later' ? 'later' : 'earlier'}.
                  </p>
                )}
              </div>
            </div>
          ) : mode === 'specific' ? (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                New Project Start Date
              </label>
              <input
                type="date"
                value={specificDate}
                onChange={(e) => setSpecificDate(e?.target?.value)}
                className="w-full px-4 py-2.5 bg-input border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
              />
              <p className="mt-1.5 text-xs text-muted-foreground">
                All items will shift so the earliest start date aligns with this date.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Update the project's start and end dates to match the earliest start date and latest end date across all deliverables, tasks, and subtasks.
              </p>
              <div className="rounded-lg border border-border bg-muted/40 divide-y divide-border">
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="CalendarArrowUp" size={14} />
                    <span>New Project Start</span>
                  </div>
                  <span className={`text-sm font-medium ${earliestStart ? 'text-foreground' : 'text-muted-foreground italic'}`}>
                    {earliestStart ? formatDate(earliestStart) : 'No dates found'}
                  </span>
                </div>
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="CalendarArrowDown" size={14} />
                    <span>New Project End</span>
                  </div>
                  <span className={`text-sm font-medium ${latestEnd ? 'text-foreground' : 'text-muted-foreground italic'}`}>
                    {latestEnd ? formatDate(latestEnd) : 'No dates found'}
                  </span>
                </div>
              </div>
              {!earliestStart && !latestEnd && (
                <p className="text-xs text-amber-600 flex items-center gap-1.5">
                  <Icon name="AlertTriangle" size={13} />
                  No dated items found in this project.
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <Icon name="AlertCircle" size={14} className="text-red-600 flex-shrink-0" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <button
            onClick={handleClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg border border-border transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-accent text-accent-foreground hover:bg-accent/90 rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-accent-foreground/30 border-t-accent-foreground rounded-full animate-spin" />
                Applying...
              </>
            ) : (
              <>
                <Icon name="Check" size={14} />
                Apply Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdjustDatesModal;
